import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Session-scoped cache so we don't re-hit the backend for the same string.
const _cache = new Map(); // key: `${lang}|${text}` -> translated

/**
 * useContentTranslation — translate org-authored, dynamic content (mission
 * statements, page copy, item descriptions) into the active locale via the
 * backend AI translation service. English passes through untouched.
 *
 * @param {object} texts  { key: englishString, ... }
 * @returns {object}      { key: translatedString | englishString, ... } + { _ready }
 *
 * Usage:
 *   const t = useContentTranslation({ title: page.title, body: page.body });
 *   <h1>{t.title}</h1>
 */
export function useContentTranslation(texts) {
    const { i18n } = useTranslation();
    const lang = (i18n.language || "en").split("-")[0];
    const [out, setOut] = useState(texts);
    const [ready, setReady] = useState(lang === "en");

    // stable signature of the inputs
    const sig = JSON.stringify(texts || {});

    useEffect(() => {
        const obj = JSON.parse(sig);
        if (lang === "en") { setOut(obj); setReady(true); return; }

        const entries = Object.entries(obj).filter(([, v]) => typeof v === "string" && v.trim());
        if (!entries.length) { setOut(obj); setReady(true); return; }

        // resolve from cache first
        const resolved = {};
        const misses = [];
        for (const [k, v] of entries) {
            const ck = `${lang}|${v}`;
            if (_cache.has(ck)) resolved[k] = _cache.get(ck);
            else misses.push([k, v]);
        }
        if (!misses.length) { setOut({ ...obj, ...resolved }); setReady(true); return; }

        let cancelled = false;
        setReady(false);
        axios.post(`${API}/i18n/translate`, {
            texts: misses.map(([, v]) => v),
            target_lang: lang,
        }).then(({ data }) => {
            if (cancelled) return;
            const map = data.translations || {};
            for (const [k, v] of misses) {
                const tr = map[v] || v;
                _cache.set(`${lang}|${v}`, tr);
                resolved[k] = tr;
            }
            setOut({ ...obj, ...resolved });
            setReady(true);
        }).catch(() => {
            if (cancelled) return;
            setOut(obj); // graceful: show English
            setReady(true);
        });
        return () => { cancelled = true; };
    }, [sig, lang]);

    return { ...out, _ready: ready };
}
